import React, { useState, useEffect, useMemo, useRef } from 'react';
import SiteIndentFormModal from './SiteIndentFormModal'; 
import SiteIndentViewModal from './SiteIndentViewModal';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { createPortal } from 'react-dom';

// --- SVG ICONS ---
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);

// Success Icon Component (Add with other icons or inside component)
const SuccessIcon = ({ size = 48, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);
const CopyIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);



const API_URL = '/api';

// --- EXACT COPY OF SEARCHABLE DROPDOWN FROM MRN ---
// --- EXACT COPY OF SEARCHABLE DROPDOWN FROM MRN ---
const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
   // Inside SearchableDropdown component...

   // UPDATE THIS useEffect IN SearchableDropdown
    useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setSearchTerm('');
      setHighlightedIndex(-1);
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = (getOptionLabel(option) || '').toString().toLowerCase();
      const secondaryLabel = (getOptionSecondaryLabel(option) || '').toString().toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);


  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0;
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1;
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

   const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);

    // --- FIX: FORCE FOCUS BACK TO THE INPUT ---
    // Since the dropdown is in a Portal, we must manually return focus 
    // to the trigger input so the user can Tab to the next field.
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // FIX: Cast to String to handle Int vs String mismatch
  const selectedOption = options.find(option => String(getOptionValue(option)) === String(value));
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center  ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        style={{ borderColor: error ? 'rgb(248, 113, 113)' : 'rgb(230, 217, 204)' }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {/* --- FIX: WRAP DROPDOWN IN CREATE PORTAL --- */}
      {isOpen && createPortal(
        <div 
           className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}

          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${String(value) === String(getOptionValue(option)) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>,
        document.body // <--- This renders the dropdown directly to the body, bypassing the modal's transform
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
  };

// -------------------------------------------------

const SurveyBOQFormModal = ({ open, onClose, initialData, onSubmit ,onFormSuccess}) => {
  
  // --- STATE ---
  const initialHeader = {
    SurveyBOQID: null,
    SurveyBOQNo: '',
    BOQDate: new Date().toISOString().split('T')[0],
    ProjectID: '',
    ProjectCode: '',
    FeederSiteID: '', // This is the Survey Feeder (Target)
    FeederCode: '',
    Status: 'Open',
    Circle: '',
    District: '',
    Division: '',
    SubDivision: '',
    SubStation: '',
    ParentSiteCode: '',
    SiteEngineer: '',
    Approval: '', // Ensure Approval is tracked
    SendToBc: false ,
    ApprovalFlowId:null
  };

  const initialLine = {
    ItemNo: '',
    PlanningLineType: '',
    ItemDescription: '',
    BOQQty: 0,
    SurveyQuantity: 0,
    QtyToIndent: 0,
    QtyIndented: 0,
    TotalIndentQty: 0,
    UOM: 'NOS',
    ProjectLineId: null,       
    ProjectLineNo: null,
    RemainingQty: null,          
    OriginalSurveyQty: 0,
    LineId: null, // Added to ensure mapping works correctly
    TotalIndentedQty: 0,
    StartDate: null,
    EndDate: null,
     Remark: '' ,
      ActivityId: null,    // <--- ADD THIS
    SubActivityId: null  
  };

  const [header, setHeader] = useState(initialHeader);
  const [lines, setLines] = useState([initialLine]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // UI State
  const [isMaximized, setIsMaximized] = useState(false);

  // Dropdown Data
  const [projects, setProjects] = useState([]);
   const [projectActivities, setProjectActivities] = useState([]);
  const [feeders, setFeeders] = useState([]);
  const [nextBOQNumber, setNextBOQNumber] = useState('');
  const [users, setUsers] = useState([]); 
  const [allProjectFeeders, setAllProjectFeeders] = useState([]); 
  
  // --- NEW STATE FOR SITE INDENT FUNCTIONALITY ---
  const [vendors, setVendors] = useState([]);
  const [indentVendors, setIndentVendors] = useState([]);
  const [isIndentPopupOpen, setIsIndentPopupOpen] = useState(false);
  // feederId here represents the Source Feeder user selects in popup
  const [indentSelection, setIndentSelection] = useState({ vendorCode: '', sourceFeederId: '' }); 
  const [siteIndentOpen, setSiteIndentOpen] = useState(false);
  const [siteIndentInitialData, setSiteIndentInitialData] = useState(null);

   // --- NEW STATE FOR VIEWING INDENTS ---
  const [indentsForBOQ, setIndentsForBOQ] = useState([]); // List of indents
  const [isIndentListOpen, setIsIndentListOpen] = useState(false); // Toggle list popup
  const [viewIndentData, setViewIndentData] = useState(null); // Data for the View Modal
  const [isViewIndentOpen, setIsViewIndentOpen] = useState(false); // Toggle View Modal

  const [selectedLines, setSelectedLines] = useState(new Set());

  // ADD THIS STATE:
const [highlightedRows, setHighlightedRows] = useState([]);
const [isRefreshingLines, setIsRefreshingLines] = useState(false);
const [selectedRowIndex, setSelectedRowIndex] = useState(null);

// 🔹 COLUMN RESIZE ADDITION
const [columnWidths, setColumnWidths] = useState({
  itemNo: 120,
  type: 100,
  description: 280,
  projectQty: 200,
  surveyQty: 200,
  startDate: 200,
  endDate: 200,
  toIndent: 200,
  indented: 200,
  uom: 80,
  remark: 280,
  activity:200,
  subActivity:200
});

const resizingRef = useRef(null);

  // --- TOAST / ALERT STATE ---
const [toast, setToast] = useState({
  open: false,
  type: 'error',
  title: '',
  message: ''
});

  
  // Store lines from selected project
  const [projectLines, setProjectLines] = useState([]);
  
  // Store full selected project object
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Flag to track if user manually changed project
  const [isManualProjectChange, setIsManualProjectChange] = useState(false);

    const isEditMode = Boolean(initialData);

  // --- UPDATED LOGIC: DISABLE FIELDS BASED ON APPROVAL STATUS ---
  
  const isSentToBC = header.SendToBc === true || header.SendToBc === 1;

  // 1. Get Status from DocumentApproval table (sent from updated backend)
  // If undefined, fallback to Header.Approval, then Header.Status
  const rawStatus = header.ApprovalStatus || header.Approval || header.Status || '';
  const approvalStatus = String(rawStatus).toUpperCase();

  // 2. Get CurrentLevel from DocumentApproval table
  // If CurrentLevel > 1, it implies Level 1 is approved/done.
  const currentLevel = parseInt(header.CurrentLevel || 0);

  // 3. Determine if fields should be read-only
  // Lock if:
  //   - Explicitly APPROVED in the approval table
  //   - OR CurrentLevel is > 1 (We are past Level 1)
  //   - OR Sent to BC is true
  const isApproved = approvalStatus === 'APPROVED' || currentLevel > 1;
  const disableStandardFields = isEditMode && ( isSentToBC);

  // 4. Qty To Indent: Disabled until Sent to BC
  const disableQtyToIndent = !isSentToBC;

  // 5. Create Indent Button: Show only if Sent to BC
  const showCreateIndentButton = isEditMode && isSentToBC;


const flowId = header.ApprovalFlowId;
const hasApprovalFlow = flowId != null && flowId !== '' && flowId !== 0;

// 4. DEBUGGING
console.log("Lock Check:", {
  isSentToBC,
  approvalStatus,
  hasApprovalFlow,
  rawFlowId: flowId, 
  result: !isSentToBC && hasApprovalFlow && approvalStatus === 'APPROVED'
});

// 5. Final Lock Condition
const isLockedApprovedNotSent = 
  !isSentToBC && 
  hasApprovalFlow && 
  approvalStatus === 'APPROVED';

  // --- API HELPER: FETCH REMAINING QTY ---
  const fetchLineRemainingQty = async (projectId, projectLineId) => {
    if (!projectId || !projectLineId) return 0;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/project/${projectId}/line/${projectLineId}/remaining-qty`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return 0;
      const data = await res.json();
      return data.data ? (data.data.RemainingQty || 0) : 0;
    } catch (e) {
      // console.error("Error fetching remaining qty:", e);
      return 0;
    }
  };

    // --- ADD THIS NEW FUNCTION ---
 // Find the fetchProjectLines function and update it

const fetchProjectLines = async (projectNo) => {
  if (!projectNo) {
    setProjectLines([]);
    return [];
  }
  try {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${API_URL}/project/${projectNo}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!res.ok) throw new Error('Failed to fetch project lines');

    const data = await res.json();
    const items = data.Items || [];
    setProjectLines(items); // Still update state for other dependencies
    
    // --- ADD THIS RETURN ---
    return items; 
  } catch (e) {
    setProjectLines([]);
    return [];
  }
};


  // --- NEW FUNCTION TO FETCH ALL FEEDERS FOR INDENT ---
  const fetchAllProjectFeeders = async (projectNo) => {
    if (!projectNo) { 
      setAllProjectFeeders([]); 
      return; 
    }
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/project/${projectNo}/feeders-filter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch all feeders');
      
      const data = await res.json();
      setAllProjectFeeders(Array.isArray(data) ? data : []);
    } catch (e) { 
      console.error("Error fetching all feeders:", e); 
      setAllProjectFeeders([]); 
    }
  };

  // --- EFFECTS ---
// Replace the existing useEffect([open, initialData]) with this:

useEffect(() => {
  if (open) {
    setIsMaximized(false);
    resetForm();
    fetchProjects();
    fetchUsers();
    fetchVendors();

    if (initialData) {
      // --- EDIT MODE ---
      const sourceData = initialData.header ? initialData.header : initialData;
      const engineerId = sourceData.SiteEngineer ? String(sourceData.SiteEngineer) : '';

      console.log("sourceData.ApprovalFlowI", sourceData.ApprovalFlowID);
      console.log("sourceData ", sourceData);

      setHeader({
        ...initialHeader,
        ...sourceData,
        BOQDate: sourceData.BOQDate ? sourceData.BOQDate.split('T')[0] : new Date().toISOString().split('T')[0],
        ProjectID: sourceData.ProjectID || '',
        FeederSiteID: sourceData.FeederSiteID || '',
        SiteEngineer: engineerId,
        SendToBc: sourceData.SendToBc === true || sourceData.SendToBc === 1,
        ApprovalFlowId: sourceData.ApprovalFlowID || sourceData.ApprovalFlowId || null
      });

      if (sourceData.SurveyBOQID) {
        fetchIndentsForBOQ(sourceData.SurveyBOQID);
      }

      // 1. Prepare existing lines initially
      let editLines = [];
      if (sourceData.Lines) editLines = sourceData.Lines;
      else if (initialData.lines) editLines = initialData.lines;

      const mappedInitialLines = editLines.map(l => ({
        ...l,
        RemainingQty: null, // Will be updated
        OriginalSurveyQty: l.SurveyQuantity || 0,
        TotalIndentedQty: 0, // Will be updated
        StartDate: l.StartDate ? l.StartDate.split('T')[0] : null,
        EndDate: l.EndDate ? l.EndDate.split('T')[0] : null
      }));
      
      // Set initial lines so UI shows something immediately
      setLines(mappedInitialLines);

      // 2. START LOADER
      setIsRefreshingLines(true);

      // 3. Fetch Latest Project Data & Update
      const refreshData = async () => {
        console.log("source data ", sourceData.SurveyBOQID);
        try {
           await fetchProjectFeeders(
            sourceData.ProjectCode, 
            sourceData.FeederSiteID, 
            sourceData.FeederCode,
            sourceData.SurveyBOQID
          );

           await fetchAllProjectFeeders(sourceData.ProjectCode);
           await fetchActivities(sourceData.ProjectCode);
          // Fetch feeders and project lines in parallel
          
          const freshProjectLines = await fetchProjectLines(sourceData.ProjectCode);

          // Fetch stats for existing lines
         // Just set the initial structure without waiting for API calls
          const linesWithStats = mappedInitialLines.map(l => ({
            ...l,
            RemainingQty: null,        // Will be fetched on focus
            TotalIndentedQty: 0     // Will be fetched on focus
          }));

          // --- MERGE LOGIC (Fix for Project Qty) ---
          if (freshProjectLines.length > 0) {
            const existingProjectLineIds = new Set(linesWithStats.map(l => l.ProjectLineId));

            // A. Update BOQQty (Project Qty) for existing lines
            const updatedLines = linesWithStats.map(line => {
              const masterLine = freshProjectLines.find(pl => pl.ProjectLineId === line.ProjectLineId);
              if (masterLine) {
                return {
                  ...line,
                  BOQQty: masterLine.Quantity || 0 // UPDATE QTY HERE
                };
              }
              return line;
            });

            // B. Identify new lines from project not in BOQ
            const newLines = freshProjectLines
              .filter(pl => !existingProjectLineIds.has(pl.ProjectLineId))
              .map(pl => ({
                ItemNo: pl.ItemNo || '',
                PlanningLineType: pl.Type || pl.LineType || '',
                ItemDescription: pl.Description || '',
                BOQQty: pl.Quantity || 0,
                SurveyQuantity: 0,
                QtyToIndent: 0,
                QtyIndented: 0,
                TotalIndentedQty: 0,
                UOM: pl.UOM || 'NOS',
                ProjectLineId: pl.ProjectLineId || null,
                ProjectLineNo: pl.LineNo || null,
                RemainingQty: pl.Quantity || 0,
                OriginalSurveyQty: 0,
                JobTaskNo: pl.JobTaskNo,
                isNew: true
              }));

            setLines([...updatedLines, ...newLines]);
          } else {
            setLines(linesWithStats);
          }
        } catch (err) {
          console.error("Error refreshing lines", err);
        } finally {
          // 4. STOP LOADER
          setIsRefreshingLines(false);
        }
      };

      refreshData();

    } else {
      // CREATE MODE
      fetchNextBOQNumber();

      if (header.ProjectCode) {
        fetchAllProjectFeeders(header.ProjectCode);
      }

    }
  }
}, [open, initialData]);

    // --- UPDATE THIS USE EFFECT ---
  useEffect(() => {
    if (initialData && projects.length > 0) {
      const sourceData = initialData.header ? initialData.header : initialData;
      const proj = projects.find(p => p.No === sourceData.ProjectCode);
      if (proj) {
        setSelectedProject(proj);
        fetchProjectFeeders(
            sourceData.ProjectCode, 
            sourceData.FeederSiteID, 
            sourceData.FeederCode,
            sourceData.SurveyBOQID // <--- ADD THIS 4th ARGUMENT
        );
        fetchProjectLines(sourceData.ProjectCode); // <--- ADD THIS CALL
      }
    }
  }, [projects, initialData]);

  useEffect(() => {
    if(initialData && users.length > 0) {
      const sourceData = initialData.header ? initialData.header : initialData;
      setHeader(prev => ({
        ...prev,
        SiteEngineer: sourceData.SiteEngineer ? String(sourceData.SiteEngineer) : ''
      }));
    }
  }, [users, initialData]);

  // --- AUTO-FILL EFFECT (CREATE MODE) ---
  // --- AUTO-FILL EFFECT (CREATE MODE) ---

  // Find the useEffect with dependencies [projectLines, initialData, ...]
// REMOVE the "else if (initialData ...)" block from inside it.

useEffect(() => {
  // 1. CREATE MODE ONLY (Keep this part)
  if (!initialData || isManualProjectChange) {
    if (projectLines.length > 0) {
       // ... existing create mode mapping logic ...
       // Map synchronously without API calls
      const updatedLines = projectLines.map((pl) => {
        return {
          ItemNo: pl.ItemNo || '',
          PlanningLineType: pl.Type || pl.LineType || '',
          ItemDescription: pl.Description || '', 
          BOQQty: pl.Quantity || 0,
          SurveyQuantity: 0,
          QtyToIndent: 0,
          QtyIndented: 0,
          UOM: pl.UOM || 'NOS',
          ProjectLineId: pl.ProjectLineId || null,
          ProjectLineNo: pl.LineNo || null,
          RemainingQty: 0, // Default to 0, fetch on focus
          OriginalSurveyQty: 0,
          JobTaskNo: pl.JobTaskNo 
        };
      });

      setLines(updatedLines);
    
    } else if (isManualProjectChange && projectLines.length === 0) {
      setLines([initialLine]);
    }
  }
  
  // --- REMOVE THE 'else if (initialData ...)' BLOCK FROM HERE ---
  // It is now handled in the main useEffect above using async/await
  
}, [projectLines, initialData, isManualProjectChange, header.ProjectID]);

  useEffect(() => {
  if (
    initialData &&
    header.FeederSiteID &&
    feeders.length > 0
  ) {
    const selectedFeeder = feeders.find(
      f => String(f.FeederId) === String(header.FeederSiteID)
    );

    if (selectedFeeder) {
      setHeader(prev => ({
        ...prev,
        ParentSiteCode: selectedFeeder.ParentSiteCode || ''
      }));
    }
  }
}, [feeders, header.FeederSiteID, initialData]);

  // --- KEYBOARD SHORTCUT FOR DUPLICATE LINE (Ctrl + Shift + D) ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if the modal is open and a row is actually selected
      if (open && selectedRowIndex !== null) {
        // Check for Ctrl + Shift + D (Case insensitive for 'd')
        if (e.ctrlKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
          e.preventDefault(); // Prevent default browser behavior
          handleDuplicateLine(selectedRowIndex);
        }
      }
    };

    // Attach event listener to the document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function to remove listener when component unmounts or closes
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, selectedRowIndex]); // Re-run if these values change

  // --- API HANDLERS ---
  const fetchNextBOQNumber = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/survey-boq/next-number`,{ headers: { 'Authorization': `Bearer ${token}` }});
      const data = await res.json();
      if (data && data.nextBOQNumber) setNextBOQNumber(data.nextBOQNumber);
    } catch (e) { setNextBOQNumber('BOQ-0001'); }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data || []);
    } catch (e) {  setUsers([]); }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/vendors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch vendors');
      const data = await res.json();
      setVendors(Array.isArray(data) ? data : []);
    } catch (e) {  setVendors([]); }
  }

  const fetchIndentVendors = async (feederCode) => {
    if (!feederCode) {
      setIndentVendors([]);
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      // Call the API created in the previous step
      const params = new URLSearchParams({ feederCode });
      if (header.ProjectCode) {
        params.append('projectNo', header.ProjectCode);
      }

      const res = await fetch(`${API_URL}/vendors-by-feeder?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      setIndentVendors(Array.isArray(data) ? data : []);
    } catch (e) {
       setIndentVendors([]); 
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/project-filter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch projects');
      const data = await res.json();
      setProjects(data || []);
    } catch (e) {  setProjects([]); }
  };

  const fetchActivities = async (projectNo) => {
    if (!projectNo) {
      setProjectActivities([]);
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/activity-master/project/${projectNo}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch activities');
      const data = await res.json();
      setProjectActivities(data || []);
    } catch (e) {
      console.error("Error fetching activities:", e);
      setProjectActivities([]);
    }
  };

    // --- UPDATE THIS FUNCTION ---
    // --- UPDATE THIS FUNCTION ---
     // --- UPDATE THIS FUNCTION ---
  const fetchProjectFeeders = async (projectNo, currentFeederId = null, currentFeederCode = null, currentBoqId = null) => {
    if (!projectNo) { 
      setFeeders([]); 
      return; 
    }
    try {
      const token = localStorage.getItem('authToken');
      console.log("fetch feeder ", currentBoqId);
      
      // Construct the URL
      let url = `${API_URL}/project/${projectNo}/feeders-filter-survey`;
      
      // IMPORTANT: Append the BOQ ID if it exists (Edit Mode)
      if (currentBoqId) {
        url += `?surveyBoqId=${currentBoqId}`;
      }

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch feeders');
      
      let data = await res.json();
      let feedersList = Array.isArray(data) ? data : [];

      if (currentFeederId && !feedersList.find(f => String(f.FeederId) === String(currentFeederId))) {
        console.warn("Feeder missing from API response, injecting manually.");
        feedersList.push({
          FeederId: currentFeederId,
          Code: currentFeederCode || 'Unknown Code',
          Description: '',
          Circle: header.Circle || '', 
          District: header.District || '',
          Division: header.Division || '',
          SubDivision: header.SubDivision || '',
          SubStation: header.SubStation || ''
        });
      }

      setFeeders(feedersList);
    } catch (e) { 
      console.error("Error fetching feeders:", e); 
      setFeeders([]); 
    }
  };
    // --- NEW HELPER FUNCTION ---
  const fetchLineIndentStats = async (lineId) => {
    // If creating a new BOQ (no LineId yet), indented qty is 0
    if (!lineId || !initialData) return 0; 

    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/survey-boq-line/${lineId}/indent-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return 0;
      const data = await res.json();
      return data.data ? (data.data.TotalIndentedQty || 0) : 0;
    } catch (e) {
      // console.error("Error fetching indent stats:", e);
      return 0;
    }
  };


    const fetchIndentsForBOQ = async (boqId) => {
    if (!boqId) return;
    try {
      const token = localStorage.getItem('authToken');
      // UPDATED URL to point to the new specific endpoint
      const res = await fetch(`${API_URL}/site-indent/boq/${boqId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch indents');
      const data = await res.json();
      setIndentsForBOQ(data || []);
    } catch (e) {
      // console.error("Error fetching indents for BOQ:", e);
      setIndentsForBOQ([]);
    }
  };

  // --- EVENT HANDLERS ---
  
  const handleHeaderChange = (field, value) => {
    setHeader(prev => ({ ...prev, [field]: value }));
  };

  // --- UPDATE THIS FUNCTION ---
  const handleProjectChange = (e) => {
    const projectNo = e.target.value;
    const projectObj = projects.find(p => p.No === projectNo);
    
    setSelectedProject(projectObj);
    setIsManualProjectChange(true);
    
    // Reset lines
    setLines([initialLine]);
    
    setHeader(prev => ({
      ...prev,
      ProjectID: projectObj ? projectObj.ProjectHeaderId : '',
      ProjectCode: projectNo,
      FeederSiteID: '', // Resetting feeder on project change
      FeederCode: '',
      Circle: '', District: '', Division: '', SubDivision: '', SubStation: ''
    }));

    // Call passing null for current ID (since we are changing projects, there is no current feeder yet)
    fetchProjectFeeders(projectNo, null, null); 
    fetchAllProjectFeeders(projectNo);
    fetchProjectLines(projectNo); 
    fetchActivities(projectNo);
  };

  const handleFeederChange = (e) => {
    const feederId = e.target.value;
    const selectedFeeder = feeders.find(f => f.FeederId == feederId);
    
    if (selectedFeeder) {
      setHeader(prev => ({
        ...prev,
        FeederSiteID: feederId,
        FeederCode: selectedFeeder.Code || '',
        Circle: selectedFeeder.Circle || '',
        District: selectedFeeder.District || '',
        Division: selectedFeeder.Division || '',
        SubDivision: selectedFeeder.SubDivision || '',
        SubStation: selectedFeeder.SubStation || '',
        ParentSiteCode: selectedFeeder.ParentSiteCode || ''
      }));
    } else {
      setHeader(prev => ({ 
        ...prev, 
        FeederSiteID: '', 
        FeederCode: '', 
        Circle: '', District: '', Division: '', SubDivision: '', SubStation: '' ,ParentSiteCode: '' 
      }));
    }
  };

  // New function to fetch stats only for the active line
const handleLineFocus = async (index) => {
  const line = lines[index];
  
  // Optional: Prevent refetching if data is already loaded (check if RemainingQty is > 0 or has a valid state)
  // But to ensure fresh data, we let it fetch.

  // 1. Fetch Remaining Qty
  const remQty = await fetchLineRemainingQty(header.ProjectID, line.ProjectLineId);
  
  // 2. Fetch Indent Stats (if LineId exists)
  const indentedQty = line.LineId ? await fetchLineIndentStats(line.LineId) : 0;

  // 3. Update the specific line in state
  setLines(prevLines => {
    const newLines = [...prevLines];
    if (newLines[index]) {
      newLines[index] = {
        ...newLines[index],
        RemainingQty: remQty,
        TotalIndentedQty: indentedQty
      };
    }
    return newLines;
  });
};

  const addLine = () => setLines([...lines, { ...initialLine }]);
  
  const removeLine = (index) => {
    if (lines.length > 1) setLines(lines.filter((_, i) => i !== index));
  };

   const handleLineChange = (index, field, value) => {
    const newLines = [...lines];

    if (field === 'ActivityId' || field === 'SubActivityId') {
    // Get the other value to check the pair
    const activityVal = field === 'ActivityId' ? value : newLines[index].ActivityId;
    const subActivityVal = field === 'SubActivityId' ? value : newLines[index].SubActivityId;

    if (activityVal && subActivityVal) {
      const isUnique = checkActivityCombinationUnique(index, activityVal, subActivityVal);
      if (!isUnique) {
        showToast('This Activity and Sub-Activity combination already exists for this item in another line.', 'error', 'Validation Error');
        return; // Stop the update
      }
    }
    
    // Reset SubActivity if Activity changes (existing logic)
    if (field === 'ActivityId') {
      newLines[index].SubActivityId = null;
    }
  }

    // <--- ADD THIS BLOCK: Reset SubActivity if Activity changes
    // if (field === 'ActivityId') {
    //   newLines[index].SubActivityId = null;
    // }
    
    // --- EXISTING LOGIC FOR SURVEY QTY (Keep as is) ---
      // if (field === 'SurveyQuantity') {
      //   const line = lines[index];
      //   const originalQty = parseFloat(line.OriginalSurveyQty) || 0;
      //   const remainingQty = parseFloat(line.RemainingQty) || 0;
      //   let newVal = parseFloat(value);

      //   if (isNaN(newVal)) newVal = 0;

      //   const difference = newVal - originalQty;
        
      //   if (difference > remainingQty) {
      //     const maxAllowed = originalQty + remainingQty;
      //     showToast(
      //     `Cannot exceed remaining quantity.\nYou can increase only up to ${maxAllowed.toFixed(6)}.`,
      //     'error'
      //   );

      //     newVal = maxAllowed;
      //   }

      //   const newLines = [...lines];
      //   newLines[index][field] = newVal;
        
      //   setLines(newLines);
      //   return;
      // }

    // --- NEW LOGIC FOR QTY TO INDENT ---
    if (field === 'QtyToIndent') {
      const line = lines[index];
      const surveyQty = parseFloat(line.SurveyQuantity) || 0;
      const totalIndented = parseFloat(line.TotalIndentedQty) || 0; // Already indented amount
      let newVal = parseFloat(value);

      if (isNaN(newVal)) newVal = 0;

      // Calculate the absolute maximum user can enter now
      // Limit = (Total Survey) - (Already Indented in previous Indents)
      const maxAllowed = surveyQty - totalIndented;

      if (newVal > maxAllowed) {
        showToast(
          `Indent quantity exceeds available limit.
        Remaining Available: ${maxAllowed.toFixed(6)}`,
          'warning'
        );

        newVal = maxAllowed;
      }

      const newLines = [...lines];
      newLines[index][field] = newVal;
      setLines(newLines);
      return;
    }

    // --- DEFAULT LOGIC ---
    
    newLines[index][field] = value;
    setLines(newLines);
  };

  // 1. Handler for Checkbox Toggle
const handleSelectLine = (index) => {
  const newSelected = new Set(selectedLines);
  // Use LineId if exists, otherwise use index for new lines
  const lineId = lines[index].LineId || `new-${index}`;

  if (newSelected.has(lineId)) {
    newSelected.delete(lineId);
  } else {
    newSelected.add(lineId);
  }
  setSelectedLines(newSelected);
};

// 1. Handler to validate Activity/SubActivity uniqueness
// 1. Handler to validate Item + Activity + SubActivity uniqueness
// ✅ FIXED - Correct property name
const checkActivityCombinationUnique = (currentIdx, activityId, subActivityId) => {
  // Check if any of the 3 values are missing. If so, skip validation (allow empty).
  if (!lines[currentIdx].ItemNo || !activityId || !subActivityId) return true; 

  const isDuplicate = lines.some((line, idx) => {
    // 1. Skip checking against the line being modified (self)
    if (idx === currentIdx) return false;
    
    // 2. Skip empty lines (e.g. if another line hasn't been filled yet)
    // ✅ FIXED TYPO HERE: CheckActivityCombinationUniqueubActivityId → SubActivityId
    if (!line.ItemNo || !line.ActivityId || !line.SubActivityId) return false;

    // 3. Compare ItemNo + Activity + SubActivity
    return String(line.ItemNo) === String(lines[currentIdx].ItemNo) && 
           String(line.ActivityId) === String(activityId) && 
           String(line.SubActivityId) === String(subActivityId);
  });

  return !isDuplicate;
};

// 2. Handler to Duplicate the Line
const handleDuplicateLine = (index) => {
  const lineToCopy = lines[index];
  
  // Create a new line object
  // We reset LineId (null) so backend treats it as INSERT
  // We reset SurveyQty, QtyToIndent, Activity, and SubActivity as requested
  const newLine = {
    ...lineToCopy,
    LineId: null, 
    SurveyQuantity: 0,
    QtyToIndent: 0,
    ActivityId: null,
    SubActivityId: null,
    // Keep original calculations for reference if needed, but they will be recalculated
    RemainingQty: lineToCopy.RemainingQty, 
    OriginalSurveyQty: 0 
  };

  // Insert the new line immediately below the selected line
  const newLines = [...lines];
  newLines.splice(index + 1, 0, newLine);
  
  setLines(newLines);
  
  // Optional: Show toast or auto-select the new row
  showToast('Line duplicated. Please enter Quantity and Activity.', 'success', 'Success');
};

// 3. Handler for Row Selection (Highlight)
const handleRowClick = (index) => {
  setSelectedRowIndex(index);
};



  // 2. Handler for Input Validation (Replaces standard handleLineChange for SurveyQuantity)
// ✅ UPDATED handleQtyValidation
// ✅ CORRECTED handleQtyValidation - Handles duplicate items properly
// ✅ FIXED - No more index mismatch!
const handleQtyValidation = (index, value) => {
  const newQty = parseFloat(value) || 0;
  const line = lines[index];
  
  const originalQty = parseFloat(line.OriginalSurveyQty) || 0;
  const totalIndented = parseFloat(line.TotalIndentedQty) || 0;
  
  // ============================================
  // CHECK PROJECT LINE ID
  // ============================================
  const hasValidProjectLineId = line.ProjectLineId && 
                                 line.ProjectLineId !== null && 
                                 line.ProjectLineId !== undefined &&
                                 line.ProjectLineId !== '' &&
                                 line.ProjectLineId !== 0;
  
  let maxForThisLine;
  
  if (hasValidProjectLineId) {
    
    // ============================================
    // CALCULATE TOTAL ORIGINAL QTY (Your existing logic)
    // ============================================
    const totalOriginalQty = lines.reduce((sum, l) => {
      if (!l.ProjectLineId || l.ProjectLineId === null || l.ProjectLineId === '' || l.ProjectLineId === 0) return sum;
      if (String(l.ProjectLineId) !== String(line.ProjectLineId)) return sum;
      return sum + (parseFloat(l.OriginalSurveyQty) || 0);
    }, 0);
    
    // ============================================
    // CALCULATE SIBLINGS CURRENT TOTAL (Your existing logic)
    // ============================================
    const siblingsCurrentTotal = lines.reduce((sum, l, idx) => {
      // Skip current line using index
      if (idx === index) return sum;
      
      if (!l.ProjectLineId || l.ProjectLineId === null || l.ProjectLineId === '' || l.ProjectLineId === 0) return sum;
      if (String(l.ProjectLineId) !== String(line.ProjectLineId)) return sum;
      
      return sum + (parseFloat(l.SurveyQuantity) || 0);
    }, 0);
    
    // ============================================
    // FIX: HANDLE LOADING STATE
    // ============================================
    if (line.RemainingQty === null || line.RemainingQty === undefined) {
      // Case: API hasn't returned yet (Loading).
      // We allow the user to type up to the Project Qty (BOQQty) temporarily.
      maxForThisLine = parseFloat(line.BOQQty) || 0;
    } else {
      // Case: Data is loaded.
      // Use your exact existing calculation:
      // Total pool = All Original + Remaining
      const remainingQty = parseFloat(line.RemainingQty);
      const totalPool = totalOriginalQty + remainingQty;
      
      // Max for THIS line = Pool - What others have
      maxForThisLine = totalPool - siblingsCurrentTotal;
    }
    
  } else {
    // No valid ProjectLineId - Independent line
    // Apply same fix for loading state
    if (line.RemainingQty === null || line.RemainingQty === undefined) {
       maxForThisLine = parseFloat(line.BOQQty) || 0;
    } else {
       const remainingQty = parseFloat(line.RemainingQty);
       maxForThisLine = originalQty + remainingQty;
    }
  }
  
  // ============================================
  // DETERMINE MINIMUM
  // ============================================
  const minAllowed = header.SendToBc ? totalIndented : 0;
  
  // ============================================
  // VALIDATE MAXIMUM
  // ============================================
  if (newQty > maxForThisLine) {
    // If data is loading, we cap it silently or show a temp message. 
    // If data is loaded, we show the strict error.
    
    // Only show toast if we are sure about the limit (data loaded) OR if they exceed Project Qty
    if (line.RemainingQty !== null && line.RemainingQty !== undefined) {
        const correctedMax = Math.max(minAllowed, Math.max(0, maxForThisLine));
        showToast(
          `Cannot exceed available quantity.\nMaximum for this line: ${correctedMax.toFixed(6)}`,
          'error',
          'Quantity Exceeded'
        );
    }
    
    // Cap the value
    const correctedMax = Math.max(minAllowed, Math.max(0, maxForThisLine));
    handleLineChange(index, 'SurveyQuantity', correctedMax);
    return;
  }
  
  // ============================================
  // VALIDATE MINIMUM
  // ============================================
  if (newQty < minAllowed) {
    showToast(
      `Quantity cannot be less than indented quantity (${totalIndented.toFixed(6)}).`,
      'error',
      'Validation Error'
    );
    handleLineChange(index, 'SurveyQuantity', minAllowed);
    return;
  }
  
  // If all validations pass, update the value
  handleLineChange(index, 'SurveyQuantity', newQty);
};

  // 3. Handler for onBlur validation
// ✅ KEEP THIS AS IS (final safety check on blur)
const handleQtyBlur = (index, value) => {
  const line = lines[index];
  const newQty = parseFloat(value) || 0;
  const totalIndented = parseFloat(line.TotalIndentedQty) || 0;
  
  // Only enforce minimum if Sent to BC
  if (header.SendToBc && newQty < totalIndented) {
    showToast(
      `Quantity cannot be less than Indented Quantity (${totalIndented.toFixed(6)}). It has been auto-corrected.`,
      'error',
      'Validation Error'
    );
    handleLineChange(index, 'SurveyQuantity', totalIndented);
  }
};


  // --- UPDATED HANDLERS FOR SITE INDENT ---
  
  const handleOpenIndentPopup = () => {
    const hasItems = lines.some(l => parseFloat(l.QtyToIndent) > 0);
    if (!hasItems) {
      // alert("Please enter 'Qty To Indent' for at least one item.");
      showToast(
        "Please enter 'Qty To Indent' for at least one item.",
        'error'
      );
      return;
    }
    fetchIndentVendors(header.FeederCode);
    // Reset selection
    setIndentSelection({ vendorCode: '', sourceFeederId: '' });
    setIsIndentPopupOpen(true);
  };

    const handleConfirmIndentSelection = () => {
    const { vendorCode, sourceFeederId } = indentSelection;

    if (!vendorCode || !sourceFeederId) {
      showToast(
        "Please select both a Contractor (Vendor) and a Source Feeder.",
        'error'
      );
      return;
    }

    // 1. Filter lines first to validate before mapping
    const rawLinesToIndent = lines.filter(l => parseFloat(l.QtyToIndent) > 0);

    if (rawLinesToIndent.length === 0) {
       showToast("No items with quantity to indent found.", 'error');
       return;
    }

    // 2. VALIDATION: Check Activity & Sub-Activity Consistency
    const firstLine = rawLinesToIndent[0];
    
    // Check if Activity/SubActivity are selected on the first item
    if (!firstLine.ActivityId || !firstLine.SubActivityId) {
      showToast("Please select an Activity and Sub-Activity for the items.", "error");
      return;
    }

    // Check if ALL items match the first item's Activity and Sub-Activity
    const allMatch = rawLinesToIndent.every(line => 
      String(line.ActivityId) === String(firstLine.ActivityId) &&
      String(line.SubActivityId) === String(firstLine.SubActivityId)
    );

    if (!allMatch) {
      showToast("All selected items must have the same Activity and Sub-Activity.", "error");
      return;
    }

    // 3. Map lines for the API payload (Only if validation passes)
    const linesToIndent = rawLinesToIndent.map((l, index) => ({
      ProductCode: l.ItemNo,
      Description: l.ItemDescription,
      Quantity: parseFloat(l.SurveyQuantity),
      UnitOfMeasure: l.UOM,
      SurveyBOQLineID: l.LineId || l.ProjectLineId, 
      QtyToIssue: parseFloat(l.QtyToIndent),
      LineNumber: 100 + (index * 100),
      TaskNo: l.JobTaskNo,
      Remark: l.Remark,
      ActivityId: l.ActivityId,
      SubActivityId: l.SubActivityId
    }));

    // 4. Construct Header Data
    const sourceFeederObj = feeders.find(f => f.FeederId == sourceFeederId);
    const targetFeederObj = feeders.find(f => f.FeederId == header.FeederSiteID);

    const indentData = {
      header: {
        ProjectNo: header.ProjectCode,           
        ProjectHeaderId: header.ProjectID,
        VendorCode: vendorCode,                   
        FromFeederId: sourceFeederId,             
        ToFeederId: header.FeederSiteID ? parseInt(header.FeederSiteID) : null,          
        SurveyBOQID: header.SurveyBOQID,
        
        // --- AUTOFILL ACTIVITY/SUBACTIVITY IN HEADER ---
        ActivityId: firstLine.ActivityId,
        SubActivityId: firstLine.SubActivityId,
        // ----------------------------------------------

        // Location auto-fill
        Circle: targetFeederObj?.Circle || header.Circle,
        District: targetFeederObj?.District || header.District,
        Division: targetFeederObj?.Division || header.Division,
        SubDivision: targetFeederObj?.SubDivision || header.SubDivision,
        SubStation: targetFeederObj?.SubStation || header.SubStation,
      },
      lines: linesToIndent
    };

    setSiteIndentInitialData(indentData);
    setIsIndentPopupOpen(false); 
    setSiteIndentOpen(true);     
  };

  const handleCloseSiteIndent = () => {
    setSiteIndentOpen(false);
    setSiteIndentInitialData(null);
    setIndentSelection({ vendorCode: '', sourceFeederId: '' });
  };

  const resetForm = () => {
    setHeader(initialHeader);
    setLines([initialLine]);
    setFeeders([]);
    setProjectLines([]);
    setNextBOQNumber('');
    setIsManualProjectChange(false);
    setSelectedProject(null);
    setIsIndentPopupOpen(false);
  };

    const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    

    if(!header.ProjectID) {
      showToast('Please select a Project before submitting.', 'error');
      return;
    }
    if(!header.FeederSiteID) {  
      showToast('Please select a Survey Feeder before submitting.', 'error');
      return;
    } 

    setIsSubmitting(true);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('authToken');
      
      // --- DECIDE URL AND PAYLOAD BASED ON SendToBc STATUS ---
      let url = '';
      let payloadLines = [];

      if (header.SendToBc) {
        // CASE: Sent to BC -> Use NEW API and Filter Lines
        
        // Filter: Only send lines that are ticked (selected) OR are new lines
        payloadLines = lines.filter((l, idx) => {
          const lineId = l.LineId || `new-${idx}`;
          const isSelected = selectedLines.has(lineId);
          const isNewLine = !l.LineId; // Lines without LineId are new
          
          return isSelected || isNewLine;
        });

        if (payloadLines.length === 0) {
          showToast('Please select at least one line to update or insert.', 'error');
          setIsSubmitting(false); 
          setLoading(false);
          return;
        }

        // Use the NEW backend endpoint
        const editId = header.SurveyBOQID;
        url = `${API_URL}/mapping/survey-boq/update-after-bc/${editId}`;
        
      } else {
        // CASE: Not Sent to BC -> Use EXISTING API and All Lines
        const sourceData = initialData && initialData.header ? initialData.header : initialData;
        const editId = sourceData ? sourceData.SurveyBOQID : null;
        
        url = initialData ? `${API_URL}/survey-boq/${editId}` : `${API_URL}/survey-boq`;
        payloadLines = lines; // Send all lines
      }

      const payload = {
        header: {
          ...header,
          SurveyBOQNo: initialData ? header.SurveyBOQNo : nextBOQNumber,
          ProjectID: header.ProjectID ? parseInt(header.ProjectID) : null,
          FeederSiteID: header.FeederSiteID ? parseInt(header.FeederSiteID) : null,
          SiteEngineer: header.SiteEngineer ? parseInt(header.SiteEngineer) : null
        },
        lines: payloadLines.map(l => ({
          ...l,
          BOQQty: parseFloat(l.BOQQty) || 0,
          SurveyQuantity: parseFloat(l.SurveyQuantity) || 0,
          QtyToIndent: parseFloat(l.QtyToIndent) || 0,
          QtyIndented: parseFloat(l.QtyIndented) || 0
        }))
      };

      // Determine method: The new API is always PUT. Existing Create is POST, Edit is PUT.
      const method = (!header.SendToBc && !initialData) ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json();
        console.log("error message ", errorData);
        
        // Create a custom error that includes the full response data
        const error = new Error(errorData.message || 'Failed to save data.');
        error.errors = errorData.errors || null;
        throw error;
      }

      const result = await res.json(); // Parse response to get ID/No

      if (onFormSuccess) { 
        onFormSuccess(result, initialData ? 'Updated' : 'Created');
      }

      // 2. Close the form immediately
      onClose();
    
    } catch (error) {
      // Check if it's a validation error with line details
      if (error.message && error.message.includes('exceeds') && error.errors) {
          // Update lines with correct RemainingQty and highlight
          const newLines = [...lines];
          const newHighlightedRows = [];
          
          error.errors.forEach(err => {
              const lineIndex = newLines.findIndex(l => l.ProjectLineId === err.ProjectLineId);
              if (lineIndex !== -1) {
                  newLines[lineIndex].RemainingQty = err.RemainingQty;
                  newHighlightedRows.push(lineIndex);
              }
          });
          
          setLines(newLines);
          setHighlightedRows(newHighlightedRows);
          
          // Build detailed error message
          const errorMessages = error.errors.map(err => 
              `Item ${err.ItemNo}: Requested ${err.RequestedQty}, Available ${err.RemainingQty}, Excess ${err.ExcessQty}`
          ).join('\n');
          
          showToast(errorMessages, 'error', 'Quantity Exceeded');
      } else {
          showToast(error.message || 'Error saving data. Please try again.', 'error');
      }
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const showToast = (message, type = 'error', title = 'Validation Error', duration = 4000) => {
  setToast({ open: true, message, type, title });

  setTimeout(() => {
    setToast(prev => ({ ...prev, open: false }));
  }, duration);
};

// 🔹 COLUMN RESIZE ADDITION
const startResize = (columnKey, startX) => {
  resizingRef.current = {
    columnKey,
    startX,
    startWidth: columnWidths[columnKey]
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', stopResize);
};

const handleMouseMove = (e) => {
  if (!resizingRef.current) return;

  const { columnKey, startX, startWidth } = resizingRef.current;
  const delta = e.clientX - startX;

  setColumnWidths(prev => ({
    ...prev,
    [columnKey]: Math.max(60, startWidth + delta)
  }));
};

const stopResize = () => {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', stopResize);
  resizingRef.current = null;
};

// 🔹 COLUMN RESIZE ADDITION
const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
  <th
    style={{ width }}
    className={`relative p-2 ${align === 'right' ? 'text-right' : ''} select-none`}
  >
    {children}
    <div
      className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400"
      onMouseDown={(e) => onResize(e.clientX)}
    />
  </th>
);


  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

   const availableSourceFeeders = useMemo(() => {
  // If no target feeder selected yet, show ALL project feeders
  if (!header.FeederSiteID) return allProjectFeeders;

  // Otherwise, filter the ALL list to remove the TARGET feeder
  return allProjectFeeders.filter(
    f => String(f.FeederId) !== String(header.FeederSiteID)
  );
}, [allProjectFeeders, header.FeederSiteID]);


  const hasIndentableItems = lines.some(l => parseFloat(l.QtyToIndent) > 0);

  if (!open) return null;

  return (
    <>
      <div className={`fixed inset-0 z-40 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>
          {`
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .custom-scrollbar::-webkit-scrollbar { height: 8px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            button:focus, select:focus, input:focus, textarea:focus { outline: 2px solid #4299e1; outline-offset: 2px; }
            .uppercase-input { text-transform: uppercase; }
            /* Chrome, Edge, Safari */
            input[type=number]::-webkit-outer-spin-button,
            input[type=number]::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }

            /* Firefox */
            input[type=number] {
              -moz-appearance: textfield;
            }

            @keyframes toastIn {
            from {
              opacity: 0;
              transform: translate(-50%, -16px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translate(-50%, 0) scale(1);
            }
          }

          @keyframes toastProgress {
            from { width: 100%; }
            to { width: 0%; }
          }

          .animate-toastIn {
            animation: toastIn 0.25s ease-out;
          }

          .animate-toastProgress {
            animation: toastProgress 4s linear forwards;
          }
            // ADD THIS INSIDE THE STYLE TAG:
            .highlight-row {
                animation: highlightPulse 2s ease-in-out;
            }

            @keyframes highlightPulse {
                0%, 100% { background-color: rgba(254, 226, 226, 0.5); }
                50% { background-color: rgba(254, 226, 226, 1); }
            }
            
            .resizable-table {
              table-layout: fixed;
              min-width: max-content;
            }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${ isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl' }`} tabIndex={0}>
          
          {/* --- MODAL HEADER --- */}
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">
                {initialData ? 'Edit Survey BOQ' : 'Create Survey BOQ'}
              </h2>
              <div className="flex items-center gap-2">
                 <div className="bg-[#F0F9FF] border border-[#BAE6FD] text-[#0C4A6E] text-xs px-4 py-2.5 rounded-lg flex items-center gap-2 ">
                <CopyIcon size={14} />
                <span className="font-medium">Tip:</span>
                <span>
                  Click a row to select it. Use <kbd className="bg-white border border-[#BAE6FD] rounded px-1.5 py-0.5 font-mono shadow-sm text-[#0284C7]">Ctrl</kbd> + <kbd className="bg-white border border-[#BAE6FD] rounded px-1.5 py-0.5 font-mono shadow-sm text-[#0284C7]">Shift</kbd> + <kbd className="bg-white border border-[#BAE6FD] rounded px-1.5 py-0.5 font-mono shadow-sm text-[#0284C7]">D</kbd> to duplicate the line.
                </span>
              </div>
                {/* NEW BUTTON: View Site Indent */}
                {initialData && (
                  <button 
                    type="button"
                    onClick={() => setIsIndentListOpen(true)}
                    className="px-3 py-1.5 text-sm border border-[#6A89A7] text-[#6A89A7] rounded-lg hover:bg-[#6A89A7] hover:text-white transition-colors"
                  >
                    View Site Indent
                  </button>
                )}
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={toggleMaximize}>
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>

          {/* --- FORM BODY --- */}
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <form onSubmit={handleSubmit} className="space-y-6 pb-6">
              
              {/* --- HEADER SECTION --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Survey BOQ Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">BOQ No</label>
                    <input type="text" value={initialData ? header.SurveyBOQNo : nextBOQNumber} disabled className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase-input text-gray-600" />
                  </div>

                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">BOQ Date</label>
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      value={header.BOQDate ? dayjs(header.BOQDate) : null}
      onChange={(newValue) => {
        handleHeaderChange('BOQDate', newValue ? newValue.format('YYYY-MM-DD') : '');
      }}
      format="DD/MM/YYYY"
      disabled={disableStandardFields}
      slotProps={{
        textField: {
          size: 'small',
          className: 'w-full',
          required: true
        }
      }}
    />
  </LocalizationProvider>
</div>

                  {/* Status - EXISTING FIELD */}
{/* <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Status</label>
  <select value={header.Status} onChange={e => handleHeaderChange('Status', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] disabled:bg-gray-100 disabled:cursor-not-allowed">
    <option value="Open">Open</option>
    <option value="Released">Released</option>
    <option value="Canceled">Canceled</option>
    <option value="Rejected">Rejected</option>
    <option value="Approved">Approved</option>
  </select>
</div> */}

{/* ADD THIS NEW FIELD - Approval/BC Status */}
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]"> Status</label>
  <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F1F5F9]">
    {!initialData ? (
      // CREATE MODE - Show Open
      <span className="text-xs font-medium text-gray-600 italic">Open</span>
    ) : header.ApprovalStatus === 'PENDING' ? (
      // EDIT MODE - Pending Approval
      <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
        Pending Approval (Level {header.CurrentLevel || 1})
      </span>
    ) : header.ApprovalStatus === 'REJECTED' ? (
      // EDIT MODE - Rejected
      <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
        Rejected
      </span>
    ) : header.ApprovalStatus === 'APPROVED' ? (
      // EDIT MODE - Approved
      (header.SendToBc === 1 || header.SendToBc === true) ? (
        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
          Sent to BC Successfully
        </span>
      ) : (
        <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
          Approved (Ready to Send BC)
        </span>
      )
    ) : (
      // EDIT MODE - No approval workflow or other status
      <span className="text-xs text-gray-500 italic">
        {(header.SendToBc === 1 || header.SendToBc === true) ? 'Sent to BC Successfully' : 'Open'}
      </span>
    )}
  </div>
</div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Site Engineer</label>
                    <select value={header.SiteEngineer} onChange={e => handleHeaderChange('SiteEngineer', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <option value="">Select Engineer</option>
                      {users.map(u => (
                        <option key={u.UserID} value={u.UserID}>{u.FullName} - {u.Username}</option>
                      ))}
                    </select>
                  </div>
        
                   <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project <span className='text-red-600'>*</span></label>

                  {/* --- UPDATED: PROJECT SEARCHABLE DROPDOWN --- */}
                  <SearchableDropdown
                    label="Project *"
                    options={projects}
                    value={header.ProjectCode}
                    onChange={(val) => handleProjectChange({ target: { value: val } })}
                    placeholder="Select Project"
                    getOptionLabel={(p) => `${p.No} - ${p.Description}`}
                    getOptionValue={(p) => p.No}
                    getOptionSecondaryLabel={(p) => p.No}
                    disabled={disableStandardFields}
                  />
                  </div>

                   <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Feeder (Survey Location) <span className='text-red-600'>*</span></label>

                  {/* --- UPDATED: FEEDER SEARCHABLE DROPDOWN --- */}
                  <SearchableDropdown
                    label="Feeder (Survey Location) *"
                    options={feeders}
                    value={header.FeederSiteID}
                    onChange={(val) => handleFeederChange({ target: { value: val } })}
                    placeholder="Select Feeder"
                    getOptionLabel={(f) => `${f.Code} - ${f.Description}`}
                    getOptionValue={(f) => f.FeederId}
                    getOptionSecondaryLabel={(f) => f.Code}
                    disabled={!header.ProjectCode || disableStandardFields}
                  />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project Description</label>
                    <input type="text" value={selectedProject?.Description || ''} readOnly className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] text-gray-600" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project Manager</label>
                    <input type="text" value={selectedProject?.ProjectManager || ''} readOnly className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] text-gray-600" />
                  </div>

                  {/* Location Fields */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Circle</label>
                    <input type="text" value={header.Circle} onChange={e => handleHeaderChange('Circle', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input disabled:bg-gray-100 disabled:cursor-not-allowed" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">District</label>
                    <input type="text" value={header.District} onChange={e => handleHeaderChange('District', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input disabled:bg-gray-100 disabled:cursor-not-allowed" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Division</label>
                    <input type="text" value={header.Division} onChange={e => handleHeaderChange('Division', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input disabled:bg-gray-100 disabled:cursor-not-allowed" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Division</label>
                    <input type="text" value={header.SubDivision} onChange={e => handleHeaderChange('SubDivision', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input disabled:bg-gray-100 disabled:cursor-not-allowed" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Station</label>
                    <input type="text" value={header.SubStation} onChange={e => handleHeaderChange('SubStation', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input disabled:bg-gray-100 disabled:cursor-not-allowed" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Parent Feeder</label>
                    <input type="text" value={header.ParentSiteCode || ''} onChange={e => handleHeaderChange('ParentSiteCode', e.target.value)} readOnly className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] text-gray-600" />
                  </div>
                </div>
              </div>

              {/* --- LINES SECTION --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">BOQ Lines</h3>
                  <div className="flex gap-2">
                    {highlightedRows.length > 0 && (
                      <button
                          type="button"
                          onClick={() => setHighlightedRows([])}
                          className="px-3 py-1.5 text-sm bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors flex items-center gap-2"
                      >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Clear Highlights ({highlightedRows.length} rows)
                      </button>
                  )}
                    {/* NEW BUTTON: Create Site Indent - ONLY SHOW IF SENT TO BC */}
                    {showCreateIndentButton && (
                      <button 
                        type="button" 
                        className={`px-4 py-2 text-white rounded-lg flex items-center transition-colors ${
                          hasIndentableItems 
                          ? 'bg-[#6A89A7] hover:bg-[#88BDF2] cursor-pointer' 
                          : 'bg-gray-400 cursor-not-allowed'
                        }`}
                        onClick={handleOpenIndentPopup}
                        disabled={!hasIndentableItems}
                        title="Enter Qty to Indent to enable"
                      >
                        Create Site Indent
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="overflow-x-auto custom-scrollbar">
                   {isRefreshingLines && (
    <div className="absolute inset-0 bg-white bg-opacity-75 z-20 flex flex-col items-center justify-center rounded-xl backdrop-blur-sm">
      <svg className="animate-spin h-10 w-10 text-[#6A89A7] mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span className="text-sm font-semibold text-[#384959]">loading...</span>
    </div>
  )}
                  <table className="resizable-table border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        {header.SendToBc && (
                        <ResizableTh width={60} onResize={(x) => startResize('select', x)}>
                          Update
                        </ResizableTh>
                      )}
                        <ResizableTh width={columnWidths.itemNo} onResize={(x) => startResize('itemNo', x)}>
                           Item No
                        </ResizableTh>
                         <ResizableTh width={columnWidths.type} onResize={(x) => startResize('type', x)}>
                           Type
                        </ResizableTh>
                         <ResizableTh width={columnWidths.description} onResize={(x) => startResize('description', x)}>
                           Description
                        </ResizableTh> 
                         <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)}>
                           UOM
                        </ResizableTh>
                         <ResizableTh width={200} onResize={(x) => startResize('activity', x)}>
                        Activity
                      </ResizableTh>
                      <ResizableTh width={200} onResize={(x) => startResize('subActivity', x)}>
                        Sub Activity
                      </ResizableTh>
                         <ResizableTh width={columnWidths.projectQty} onResize={(x) => startResize('projectQty', x)} align="right">
                           Project Qty
                        </ResizableTh>
                         <ResizableTh width={columnWidths.surveyQty} onResize={(x) => startResize('surveyQty', x)} align="right">
                           Survey Qty
                        </ResizableTh>
                         <ResizableTh width={columnWidths.startDate} onResize={(x) => startResize('startDate', x)}>
                           Start Date
                        </ResizableTh>
                         <ResizableTh width={columnWidths.endDate} onResize={(x) => startResize('endDate', x)}>
                           End Date
                        </ResizableTh>
                         <ResizableTh width={columnWidths.toIndent} onResize={(x) => startResize('toIndent', x)} align="right">
                           To Indent
                        </ResizableTh>
                         <ResizableTh width={columnWidths.indented} onResize={(x) => startResize('indented', x)} align="right">
                           Indented
                        </ResizableTh>
                        <ResizableTh width={columnWidths.remark} onResize={(x) => startResize('remark', x)}>
                        Remark
                      </ResizableTh>
                       <ResizableTh width={columnWidths.remark} onResize={(x) => startResize('remark', x)}>
                        Action
                      </ResizableTh>
                        
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, idx) =>{

                       const parentActivities = projectActivities.filter(a => a.IsParentActivity === 1 || a.IsParentActivity === true);

                        // --- 2. FIND SELECTED ACTIVITY OBJECT ---
                        // We need the object (specifically the Code) to filter children for the second dropdown
                        const selectedActivityObj = projectActivities.find(a => a.ActivityId == line.ActivityId);
                        
                        // --- 3. FILTER SUB-ACTIVITIES ---
                        // Only show activities where ParentActivityCode matches the selected Activity's Code
                        const subActivityOptions = selectedActivityObj 
                          ? projectActivities.filter(a => a.ParentActivityCode === selectedActivityObj.Code)
                          : [];

                       return (
                          <tr 
                            key={idx} 
                             onClick={() => handleRowClick(idx)} // Add this
                            className={`border-t border-[#E2E8F0] transition-colors duration-300 cursor-pointer ${
                              selectedRowIndex === idx ? 'bg-blue-50 ring-1 ring-blue-300' : 'hover:bg-gray-50' // Highlight logic
                            } ${
                              highlightedRows.includes(idx) ? 'bg-red-100 border-l-4 border-l-red-500' : ''
                            }`}
                        >

                          {header.SendToBc && (
                          <td className="p-2 border border-[#E2E8F0] text-center">
                            {line.LineId ? (
                              // EXISTING LINE: Show Checkbox
                              <input 
                                type="checkbox"
                                className="w-5 h-5 cursor-pointer"
                                checked={selectedLines.has(line.LineId)}
                                onChange={() => handleSelectLine(idx)}
                              />
                            ) : (
                              // NEW LINE: Show "New" Badge (No checkbox needed)
                              <span className="px-2 py-1 text-xs font-bold text-white bg-blue-500 rounded">
                                New
                              </span>
                            )}
                          </td>
                        )}
                          <td className="p-2" style={{ width: columnWidths.itemNo }}>
                            <input type="text" value={line.ItemNo} onChange={e => handleLineChange(idx, 'ItemNo', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase-input disabled:bg-gray-100 pointer-events-none" 
                            readOnly/>
                          </td>
                          <td className="p-2" style={{ width: columnWidths.type }}>
                            <input type="text" value={line.PlanningLineType} onChange={e => handleLineChange(idx, 'PlanningLineType', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase-input disabled:bg-gray-100 pointer-events-none" 
                            readOnly/>
                          </td>
                          <td className="p-2" style={{ width: columnWidths.description }}>
                            <input type="text" value={line.ItemDescription} onChange={e => handleLineChange(idx, 'ItemDescription', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] disabled:bg-gray-100 pointer-events-none" 
                            readOnly/>
                          </td>
                          <td className="p-2" style={{ width: columnWidths.uom }}>
                            <input type="text" value={line.UOM} onChange={e => handleLineChange(idx, 'UOM', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 uppercase-input disabled:bg-gray-100 pointer-events-none" readOnly />
                          </td>
                           <td className="p-2" style={{ width: columnWidths.activity }}>
                            <SearchableDropdown
                              options={parentActivities} 
                              value={line.ActivityId}
                              onChange={(val) => handleLineChange(idx, 'ActivityId', val)}
                              placeholder="Select Activity"
                              getOptionLabel={(a) => `${a.Code} - ${a.Description}`}
                              getOptionValue={(a) => a.ActivityId}
                              getOptionSecondaryLabel={(a) => a.Code}
                              disabled={
                              // LOGIC: If SendToBc is true, only disable if the row is NOT selected (checked)
                               header.SendToBc ? (
                          // CONDITION: If Total Indent Qty > 0, LOCK IT (Cannot edit)
                          (line.TotalIndentQty > 0) 
                            ? true 
                            // ELSE: Allow edit only if row is selected (checked)
                            : (line.LineId ? !selectedLines.has(line.LineId) : false)
                        ) 
                                : disableStandardFields
                            }
                            />
                          </td>
                            <td className="p-2" style={{ width: columnWidths.subActivity }}>
                            <SearchableDropdown
                              options={subActivityOptions} 
                              value={line.SubActivityId}
                              onChange={(val) => handleLineChange(idx, 'SubActivityId', val)}
                              placeholder="Select Sub-Activity"
                              getOptionLabel={(a) => `${a.Code} - ${a.Description}`}
                              getOptionValue={(a) => a.ActivityId}
                              getOptionSecondaryLabel={(a) => a.Code}
                             disabled={
                                  // LOGIC: Must always be disabled if no Activity is selected
                                  !line.ActivityId || 
                                  (
                                    // LOGIC: If SendToBc is true, only disable if the row is NOT selected (checked)
                                    header.SendToBc ? (
                                    // CONDITION: If Total Indent Qty > 0, LOCK IT (Cannot edit)
                                    (line.TotalIndentQty > 0) 
                                      ? true 
                                      // ELSE: Allow edit only if row is selected (checked)
                                      : (line.LineId ? !selectedLines.has(line.LineId) : false)
                                  )
                                      : disableStandardFields
                                  )
                                } 
                            />
                          </td>
                          <td className="p-2" style={{ width: columnWidths.projectQty }}>
                            <input type="number" inputMode="decimal"  step="0.000001" value={line.BOQQty} onChange={e => handleLineChange(idx, 'BOQQty', e.target.value)} disabled={disableStandardFields} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] disabled:bg-gray-100 pointer-events-none" readOnly />
                          </td>
                         <td className="p-2" style={{ width: columnWidths.surveyQty }}>
        <input 
          type="number"
          value={line.SurveyQuantity}
          // CHANGE: Use new validation handler
          onChange={(e) => handleQtyValidation(idx, e.target.value)}
              onBlur={(e) => handleQtyBlur(idx, e.target.value)}
          onFocus={(e) => {
              e.target.select();
              handleLineFocus(idx); // <--- ADD THIS CALL
            }}
          // CHANGE: Disable based on checkbox if SendToBc is true
         // LOGIC: If New Line -> Always Enabled. If Existing Line -> Needs Checkbox.
          disabled={
            // PRIORITY 1: If Approved but not sent, LOCK IT immediately
            isLockedApprovedNotSent ? true 
            
            // PRIORITY 2: Else, follow existing logic (Sent to BC checkbox or Standard Edit)
            : (header.SendToBc 
                ? (line.LineId ? !selectedLines.has(line.LineId) : false) 
                : disableStandardFields
              )
          }
          className={`w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] ${
            header.SendToBc && (!line.LineId || selectedLines.has(line.LineId))
            ? 'ring-2 ring-blue-400 border-blue-400'
            : 'disabled:bg-gray-100'
          }`}
        />
      </td>
                                {/* --- ADD THESE DATE PICKER CELLS --- */}
      <td className="p-2" style={{ width: columnWidths.startDate }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={line.StartDate ? dayjs(line.StartDate) : null}
            onChange={(newValue) => {
              handleLineChange(idx, 'StartDate', newValue ? newValue.format('YYYY-MM-DD') : null);
            }}
            format="DD/MM/YYYY"
            disabled={disableStandardFields}
            slotProps={{
              textField: {
                size: 'small',
                className: 'w-full',
                sx: { 
                  '& .MuiInputBase-input': { padding: '6px 8px', fontSize: '0.875rem' },
                  '& .MuiOutlinedInput-root': { backgroundColor: 'white' }
                }
              }
            }}
          />
        </LocalizationProvider>
      </td>
      
      <td className="p-2" style={{ width: columnWidths.endDate }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={line.EndDate ? dayjs(line.EndDate) : null}
            onChange={(newValue) => {
              handleLineChange(idx, 'EndDate', newValue ? newValue.format('YYYY-MM-DD') : null);
            }}
            format="DD/MM/YYYY"
            disabled={disableStandardFields}
            slotProps={{
              textField: {
                size: 'small',
                className: 'w-full',
                sx: { 
                  '& .MuiInputBase-input': { padding: '6px 8px', fontSize: '0.875rem' },
                  '& .MuiOutlinedInput-root': { backgroundColor: 'white' }
                }
              }
            }}
          />
        </LocalizationProvider>
      </td>
                          <td className="p-2" style={{ width: columnWidths.toIndent }}>
                            <input 
                              type="number"
                              inputMode="decimal" 
                              step="0.000001"
                              value={line.QtyToIndent} 
                              disabled={disableQtyToIndent}
                              onChange={e => handleLineChange(idx, 'QtyToIndent', e.target.value)} 
                              onFocus={(e) => {
                                e.target.select();
                                handleLineFocus(idx); // <--- ADD THIS CALL
                              }}
                              className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] font-semibold text-blue-600 disabled:bg-gray-100" 
                              placeholder="0"
                            />
                          </td>
                          <td className="p-2" style={{ width: columnWidths.indented }}>
                            <div className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-right bg-gray-50 text-gray-700 font-medium">
                              {line.TotalIndentQty || 0}
                            </div>
                          </td>
                          

                          
                        <td className="p-2" style={{ width: columnWidths.remark }}>
                          <input 
                            type="text"
                            value={line.Remark || ''}
                            onChange={e => handleLineChange(idx, 'Remark', e.target.value)}
                            disabled={disableStandardFields}
                            className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] disabled:bg-gray-100" 
                          />
                        </td>

                         {/* Add this column inside your <thead> as well if it's not there */}
                        <td className="p-2 text-center border border-[#E2E8F0]" style={{ width: columnWidths.remark }}>
                          <button 
                            type="button" 
                            className={`text-blue-600 hover:text-blue-800 transition-colors p-1 rounded ${
                              selectedRowIndex === idx ? 'opacity-100' : 'opacity-30' // Dim button unless row selected
                            }`}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click trigger
                              handleDuplicateLine(idx);
                            }}
                            title="Duplicate Line"
                          >
                            <CopyIcon size={18} />
                          </button>
                        </td>

                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Lines</span>
                      <span className="text-base font-semibold">{lines.length}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Survey Quantity</span>
                      <span className="text-base font-semibold text-lg text-blue-600">
                        {lines.reduce((acc, line) => acc + (parseFloat(line.SurveyQuantity) || 0), 0).toFixed(6)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* --- FOOTER --- */}
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button type="button" className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed flex items-center" disabled={loading || isSubmitting} onClick={handleSubmit}>
                {loading || isSubmitting ? 'Saving...' : (
                  <>
                    <SaveIcon className="mr-2" size={18} />
                    {initialData ? 'Update' : 'Save Survey BOQ'}
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* --- INTERMEDIATE POPUP (Select Vendor & Source Feeder) --- */}
      {isIndentPopupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-[#384959] mb-4">Select Contractor & Source Feeder</h3>
            
            <div className="space-y-4">
              {/* --- UPDATED: VENDOR SEARCHABLE DROPDOWN --- */}
              <SearchableDropdown
                label="Contractor (Vendor)"
                options={indentVendors}
                value={indentSelection.vendorCode}
                onChange={(val) => setIndentSelection({...indentSelection, vendorCode: val})}
                placeholder="Select Contractor"
                getOptionLabel={(v) => v.vendor_name}
                getOptionValue={(v) => v.vendor_code}
                getOptionSecondaryLabel={(v) => v.vendor_code}
              />

              {/* --- UPDATED: SOURCE FEEDER SEARCHABLE DROPDOWN --- */}
              <SearchableDropdown
                label="Select Source Feeder (From)"
                options={availableSourceFeeders}
                value={indentSelection.sourceFeederId}
                onChange={(val) => setIndentSelection({...indentSelection, sourceFeederId: val})}
                placeholder="Select Source Feeder"
                getOptionLabel={(f) => `${f.Code} - ${f.Description}`}
                getOptionValue={(f) => f.FeederId}
                getOptionSecondaryLabel={(f) => f.Code}
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => setIsIndentPopupOpen(false)} 
                className="px-4 py-2 rounded-lg text-[#64748b] hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleConfirmIndentSelection}
                className="px-4 py-2 rounded-lg bg-[#6A89A7] text-white hover:bg-[#88BDF2] transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

            {/* --- SITE INDENT LIST POPUP --- */}
      {isIndentListOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-bold text-[#384959] mb-4">Site Indents for this BOQ</h3>
            
            {indentsForBOQ.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">No Site Indents created for this BOQ yet.</p>
            ) : (
              <div className="max-h-60 overflow-y-auto custom-scrollbar border border-gray-200 rounded-lg">
                {indentsForBOQ.map(indent => (
                  <div 
                    key={indent.SiteIndentID} 
                    onClick={() => {
                      setViewIndentData({ header: indent, lines: indent.Lines });
                      setIsViewIndentOpen(true);
                      setIsIndentListOpen(false);
                    }}
                    className="flex justify-between items-center p-3 border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors last:border-0"
                  >
                    <div>
                      <div className="font-semibold text-[#384959]">{indent.SiteIndentNo}</div>
                      <div className="text-xs text-[#64748b]">Vendor: {indent.VendorCode} | Status: {indent.Status}</div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {indent.Date ? new Date(indent.Date).toLocaleDateString() : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button 
                onClick={() => setIsIndentListOpen(false)} 
                className="px-4 py-2 rounded-lg text-[#64748b] hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- SITE INDENT VIEW MODAL --- */}
      <SiteIndentViewModal 
        open={isViewIndentOpen} 
        onClose={() => setIsViewIndentOpen(false)} 
        data={viewIndentData} 
      />

      {/* --- SITE INDENT FORM MODAL --- */}
                  {/* --- SITE INDENT FORM MODAL --- */}
      <SiteIndentFormModal 
        open={siteIndentOpen} 
        onClose={handleCloseSiteIndent} 
        initialData={siteIndentInitialData} 
        onFormSuccess={(submittedData) => { // <--- CHANGED TO 'onFormSuccess'
          
          // 1. Show success toast (Using custom toast for better UI)
          showToast('Site Indent created successfully!', 'success', 'Success');
          
          // 2. Logic to reset QtyToIndent to 0 for indented items
          if (submittedData && submittedData.lines) {
            const indentedLineIds = new Set(submittedData.lines.map(l => l.SurveyBOQLineID));
            setLines(prevLines => prevLines.map(line => {
              if (line.LineId && indentedLineIds.has(line.LineId)) {
                return { ...line, QtyToIndent: 0 };
              }
              return line;
            }));
          }
          
          const boqId = submittedData?.header?.SurveyBOQID || header.SurveyBOQID;
          if (boqId) {
            fetchIndentsForBOQ(boqId); // This will now run and update the list
          }

          // 3. Close the modal
          handleCloseSiteIndent();
        }} 
      />

      {/* --- TOP CENTER TOAST --- */}
{toast.open && (
  <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
    <div
      className={`
        relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden
        ${toast.type === 'error' && 'bg-red-50/90 border-red-200'}
        ${toast.type === 'warning' && 'bg-amber-50/90 border-amber-200'}
        ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}
      `}
    >
      {/* Content */}
      <div className="flex gap-4 px-5 py-4">
        {/* Icon */}
        <div className="pt-1">
          {toast.type === 'error' && (
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
          )}

          {toast.type === 'warning' && (
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
          )}
        </div>

        {/* Text */}
        <div className="flex-1">
          <div className="font-semibold text-sm text-gray-800 mb-1">
            {toast.title}
          </div>
          <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
            {toast.message}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div
        className={`
          absolute bottom-0 left-0 h-[3px] animate-toastProgress
          ${toast.type === 'error' && 'bg-red-500'}
          ${toast.type === 'warning' && 'bg-amber-500'}
          ${toast.type === 'success' && 'bg-green-500'}
        `}
      />
    </div>
  </div>
)}




    </>
  );
};

export default SurveyBOQFormModal;